<template>
  <div
    class="selection-shadow relative w-full cursor-pointer overflow-hidden rounded-[12px] bg-white px-4 py-[10px] transition-colors after:pointer-events-none after:absolute after:inset-0 after:z-[5] after:rounded-[12px] after:border after:content-['']"
    :class="
      selected
        ? 'after:border-gray-800'
        : 'after:border-[#ebebeb] hover:bg-[#f9f9fa]'
    "
    @click="handleClick"
  >
    <PlanCallout v-if="priceCallout || plan.name === 'Pro Bundle'">
      {{ priceCallout || 'Big discounts as you grow!' }}
    </PlanCallout>

    <div class="text-[14px] font-normal leading-[22px] text-gray-800">
      {{ plan.name }}
    </div>

    <div
      v-if="subscriptionPrice"
      class="flex items-baseline gap-x-[6px] text-gray-800"
    >
      <span class="text-[22px] font-medium leading-[28px]">
        {{ subscriptionPrice.renderedPrice }}
      </span>
      <span class="text-[15px] font-medium italic leading-[25px]">
        {{ subscriptionSuffix }}
      </span>
    </div>

    <div v-if="filePrice" class="flex items-baseline gap-x-[6px] text-gray-800">
      <span class="text-[18px] font-medium leading-[22px]">
        {{ filePrice.renderedPrice }}
      </span>
      <span
        v-if="fileSuffix?.label"
        class="text-[15px] font-medium italic leading-[20px]"
      >
        {{ fileSuffix.label }}
      </span>
      <span
        v-if="fileSuffix?.fine"
        class="text-[12px] font-normal italic leading-[17px]"
      >
        {{ fileSuffix.fine }}
      </span>
    </div>

    <template v-if="headerText">
      <div class="-mx-4 mt-3 h-px bg-black/[0.08]" />
      <div
        class="mt-[7px] text-[13px] font-normal leading-[18px] text-gray-600"
      >
        {{ headerText }}
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import {
  PlanItemStatus,
  PlanPriceType,
  type Plan,
} from '@/shared/graphql/legacy/generated'
import { splitFine } from '@/features/billing/utils/fine'
import { t } from '@/shared/i18n'
import PlanCallout from './PlanCallout.vue'

const props = defineProps<{
  modelValue?: Plan
  plan: Plan
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', plan: Plan): void
}>()

const selected = computed(() => props.modelValue?.id === props.plan.id)

const lowestActivePlanItem = computed(() =>
  props.plan.items.find(
    (item, index, arr) =>
      item.status === PlanItemStatus.Active || index === arr.length - 1
  )
)

const subscriptionPrice = computed(
  () =>
    lowestActivePlanItem.value?.prices.find(
      (price) => price.type === PlanPriceType.Subscription
    )?.details
)

const filePrice = computed(
  () =>
    lowestActivePlanItem.value?.prices.find(
      (price) => price.type === PlanPriceType.File
    )?.details
)

// Plan copy arrives from the gateway as translation keys.
const priceCallout = computed(() => t(props.plan.priceCallout))

const headerText = computed(() => t(props.plan.headerText))

const subscriptionSuffix = computed(() => t(subscriptionPrice.value?.suffix))

const fileSuffix = computed(() => {
  const suffix = t(filePrice.value?.suffix)
  return suffix ? splitFine(suffix) : null
})

const handleClick = () => {
  emit('update:modelValue', props.plan)
}
</script>

<style scoped>
.selection-shadow {
  box-shadow:
    0 0.8px 2.925px 0 rgba(0, 0, 0, 0.02),
    0 2.025px 7.8px 0 rgba(0, 0, 0, 0.004),
    0 4px 18px 0 rgba(0, 0, 0, 0.04);
}
</style>
